// src/services/orderService.js
import ApiService from './api'
import AppURLs from '../config/urls'

class OrderService {
  /**
   * Get all orders with filtering
   * @param {Object} params - Query parameters for filtering
   * @returns {Promise<Object>} - API response
   */
  async getAllOrders(params = {}) {
    try {
      console.log('📋 Fetching orders with params:', params)
      
      const response = await ApiService.get(AppURLs.ORDERS.GET_ALL, params)
      
      console.log('✅ Orders fetched successfully:', response)
      return response
    } catch (error) {
      console.error('❌ Error fetching orders:', error)
      throw error
    }
  }

  /**
   * Get pending orders for Hold Orders functionality
   * @param {string} branchId - Branch ID to filter orders
   * @param {Object} additionalParams - Additional query parameters
   * @returns {Promise<Object>} - API response with pending orders
   */
  async getPendingOrders(branchId, additionalParams = {}) {
    try {
      console.log('📋 Fetching pending orders for branch:', branchId)
      
      const params = {
        branch: branchId,
        orderStatus: 'pending',
        paymentStatus: 'unpaid',
        ...additionalParams
      }
      
      const response = await this.getAllOrders(params)
      
      console.log('✅ Pending orders fetched successfully:', response)
      return response
    } catch (error) {
      console.error('❌ Error fetching pending orders:', error)
      
      // Return mock data for development if API endpoint doesn't exist
      if (error.message?.includes('404') || error.message?.includes('Network Error')) {
        console.log('🔧 Returning mock data for development')
        return this.getMockPendingOrders(branchId)
      }
      
      throw error
    }
  }

  /**
   * Get mock pending orders for development
   * @param {string} branchId - Branch ID
   * @returns {Object} - Mock API response
   */
  getMockPendingOrders(branchId) {
    const mockOrders = [
      {
        _id: "mock1",
        orderNumber: "ORD10000015",
        branch: {
          _id: branchId,
          name: "Kurunegala Branch"
        },
        customer: {
          _id: "cust1",
          name: "Kamal Perera",
          mobileNumber: "0778278331"
        },
        orderType: "takeaway",
        table: null,
        deliveryProvider: null,
        orderStatus: "pending",
        orderItems: [
          {
            product: {
              _id: "prod1",
              name: "Cappuccino",
              sku: "CAP001",
              category: { name: "Hot Coffee" },
              type: "product"
            },
            quantity: 2,
            unitPrice: 250
          },
          {
            product: {
              _id: "prod2", 
              name: "Chocolate Cake",
              sku: "CAKE001",
              category: { name: "Desserts" },
              type: "product"
            },
            quantity: 1,
            unitPrice: 350
          }
        ],
        orderPayment: {
          totalAmount: 850,
          paidAmount: 0,
          remainingAmount: 850
        },
        paymentStatus: "unpaid",
        orderDate: new Date().toISOString(),
        createdAt: new Date().toISOString()
      },
      {
        _id: "mock2",
        orderNumber: "ORD10000018",
        branch: {
          _id: branchId,
          name: "Kurunegala Branch"
        },
        customer: {
          _id: "cust2",
          name: "Saman Silva",
          mobileNumber: "0758457845"
        },
        orderType: "delivery",
        table: null,
        deliveryProvider: "pickme",
        orderStatus: "pending",
        orderItems: [
          {
            product: {
              _id: "prod3",
              name: "Chicken Pizza",
              sku: "PIZZA001",
              category: { name: "Main Dishes" },
              type: "product"
            },
            quantity: 1,
            unitPrice: 1200
          }
        ],
        orderPayment: {
          totalAmount: 1250,
          paidAmount: 0,
          remainingAmount: 1250
        },
        paymentStatus: "unpaid",
        orderDate: new Date(Date.now() - 30 * 60 * 1000).toISOString(), // 30 minutes ago
        createdAt: new Date(Date.now() - 30 * 60 * 1000).toISOString()
      },
      {
        _id: "mock3",
        orderNumber: "ORD10000105",
        branch: {
          _id: branchId,
          name: "Kurunegala Branch"
        },
        customer: {
          _id: "cust3",
          name: "Nimal Perera",
          mobileNumber: "0712458745"
        },
        orderType: "dine-in",
        table: {
          _id: "table1",
          number: "5",
          capacity: 4
        },
        deliveryProvider: null,
        orderStatus: "pending",
        orderItems: [
          {
            product: {
              _id: "prod4",
              name: "Iced Latte",
              sku: "LAT002",
              category: { name: "Cold Coffee" },
              type: "product"
            },
            quantity: 2,
            unitPrice: 280
          },
          {
            product: {
              _id: "prod5",
              name: "Club Sandwich",
              sku: "SAND001",
              category: { name: "Sandwiches" },
              type: "product"
            },
            quantity: 1,
            unitPrice: 420
          }
        ],
        orderPayment: {
          totalAmount: 980,
          paidAmount: 0,
          remainingAmount: 980
        },
        paymentStatus: "unpaid",
        orderDate: new Date(Date.now() - 15 * 60 * 1000).toISOString(), // 15 minutes ago
        createdAt: new Date(Date.now() - 15 * 60 * 1000).toISOString()
      }
    ]

    return {
      success: true,
      statusCode: 200,
      message: "Mock pending orders retrieved successfully",
      data: {
        orders: mockOrders,
        page: 1,
        limit: 50,
        totalPages: 1,
        totalResults: mockOrders.length
      }
    }
  }

  /**
   * Get order by ID
   * @param {string} id - Order ID
   * @returns {Promise<Object>} - API response
   */
  async getOrderById(id) {
    try {
      console.log('📋 Fetching order by ID:', id)
      
      if (!id) {
        throw new Error('Order ID is required')
      }

      const endpoint = ApiService.replaceUrlParams(AppURLs.ORDERS.GET_BY_ID, { id })
      const response = await ApiService.get(endpoint)
      
      console.log('✅ Order fetched successfully:', response)
      return response
    } catch (error) {
      console.error('❌ Error fetching order:', error)
      throw error
    }
  }

  /**
   * Update order status
   * @param {string} id - Order ID
   * @param {string} status - New status
   * @returns {Promise<Object>} - API response
   */
  async updateOrderStatus(id, status) {
    try {
      console.log('📋 Updating order status:', id, 'to', status)
      
      if (!id) {
        throw new Error('Order ID is required')
      }

      const endpoint = ApiService.replaceUrlParams(AppURLs.ORDERS.UPDATE, { id })
      const response = await ApiService.patch(endpoint, { orderStatus: status })
      
      console.log('✅ Order status updated successfully:', response)
      return response
    } catch (error) {
      console.error('❌ Error updating order status:', error)
      throw error
    }
  }

  /**
   * Delete order (hard delete)
   * This method directly calls the DELETE /api/v1/orders/:id endpoint
   * to permanently remove the order from the database.
   * @param {string} id - Order ID (e.g., '68b5f4275658a8628cc3b968')
   * @returns {Promise<Object>} - API response
   */
  async deleteOrder(id) {
    try {
      console.log('🗑️ Deleting order:', id)
      
      if (!id) {
        throw new Error('Order ID is required')
      }

      const endpoint = ApiService.replaceUrlParams(AppURLs.ORDERS.DELETE, { id })
      console.log('📍 DELETE endpoint:', endpoint)
      
      const response = await ApiService.delete(endpoint)
      
      console.log('✅ Order deleted successfully:', response)
      return response
    } catch (error) {
      console.error('❌ Error deleting order:', error)
      throw error
    }
  }

  /**
   * Cancel/Delete order
   * @param {string} id - Order ID
   * @returns {Promise<Object>} - API response
   */
  async cancelOrder(id) {
    try {
      console.log('📋 Cancelling order:', id)
      
      if (!id) {
        throw new Error('Order ID is required')
      }

      // First try to update status to cancelled
      try {
        const response = await this.updateOrderStatus(id, 'cancelled')
        console.log('✅ Order cancelled successfully:', response)
        return response
      } catch (updateError) {
        console.warn('⚠️ Failed to update status, trying delete:', updateError)
        
        // If status update fails, try to delete
        const response = await this.deleteOrder(id)
        return response
      }
    } catch (error) {
      console.error('❌ Error cancelling order:', error)
      throw error
    }
  }

  /**
   * Format order data for POS cart
   * @param {Object} order - Order object from API
   * @returns {Object} - Formatted cart data
   */
  formatOrderForCart(order) {
    try {
      console.log('🔄 Formatting order for cart:', order)

      if (!order || !order.orderItems) {
        throw new Error('Invalid order data')
      }

      const cartItems = order.orderItems.map(item => {
        const product = item.product
        if (!product) {
          console.warn('⚠️ Order item missing product data:', item)
          return null
        }

        const cartItem = {
          id: product._id,
          name: product.name,
          price: item.unitPrice,
          quantity: parseInt(item.quantity) || 1, // Ensure quantity is a number
          category: product.category,
          sku: product.sku,
          type: product.type,
          product: product, // Keep product reference for productId extraction
          productId: product._id, // Add explicit productId
          // Include original item data for reference
          originalItem: item
        }

        // Debug log each cart item
        console.log('🔄 Creating cart item:', {
          id: cartItem.id,
          name: cartItem.name,
          quantity: cartItem.quantity,
          originalQuantity: item.quantity
        })

        return cartItem
      }).filter(Boolean) // Remove null items

      const formattedData = {
        orderId: order._id, // Add order ID for patching
        cartItems,
        orderType: order.orderType || 'dine-in',
        customer: order.customer,
        table: order.table,
        deliveryProvider: order.deliveryProvider,
        originalOrder: order
      }

      console.log('✅ Order formatted for cart:', formattedData)
      return formattedData
    } catch (error) {
      console.error('❌ Error formatting order for cart:', error)
      throw error
    }
  }

  /**
   * Get order status options
   * @returns {Array} - Array of status options
   */
  getOrderStatusOptions() {
    return [
      { value: 'pending', label: 'Pending' },
      { value: 'confirmed', label: 'Confirmed' },
      { value: 'preparing', label: 'Preparing' },
      { value: 'ready', label: 'Ready' },
      { value: 'completed', label: 'Completed' },
      { value: 'cancelled', label: 'Cancelled' }
    ]
  }

  /**
   * Get payment status options
   * @returns {Array} - Array of payment status options
   */
  getPaymentStatusOptions() {
    return [
      { value: 'unpaid', label: 'Unpaid' },
      { value: 'partial', label: 'Partially Paid' },
      { value: 'paid', label: 'Paid' },
      { value: 'refunded', label: 'Refunded' }
    ]
  }

  /**
   * Create a new order
   * @param {Object} orderData - Order data to create
   * @returns {Promise<Object>} - API response with created order
   */
  async createOrder(orderData) {
    try {
      console.log('📝 Creating order with data:', orderData)
      
      const response = await ApiService.post(AppURLs.ORDERS.CREATE, orderData)
      
      console.log('✅ Order created successfully:', response)
      return response
    } catch (error) {
      console.error('❌ Error creating order:', error)
      throw error
    }
  }

  /**
   * Update an existing order
   * @param {string} orderId - Order ID to update
   * @param {Object} orderData - Order data to update
   * @returns {Promise<Object>} - API response with updated order
   */
  async updateOrder(orderId, orderData) {
    try {
      console.log('📝 Updating order with ID:', orderId, 'data:', orderData)
      
      // Enhanced validation and data cleaning for order updates
      const cleanedOrderData = {
        ...orderData,
        orderItems: orderData.orderItems?.map(item => ({
          productId: item.productId,
          quantity: Math.max(1, Number(item.quantity) || 1) // Ensure quantity is at least 1
        })).filter(item => 
          item.productId && 
          item.quantity && 
          item.quantity > 0
        ) || []
      }
      
      console.log('📝 Cleaned order data for update:', cleanedOrderData)
      console.log('📝 Order items validation:', cleanedOrderData.orderItems.map(item => ({
        productId: item.productId,
        quantity: item.quantity,
        quantityType: typeof item.quantity,
        isValid: item.quantity > 0
      })))
      
      const url = AppURLs.ORDERS.UPDATE.replace(':id', orderId)
      const response = await ApiService.patch(url, cleanedOrderData)
      
      console.log('✅ Order updated successfully:', response)
      return response
    } catch (error) {
      console.error('❌ Error updating order:', error)
      throw error
    }
  }

  /**
   * Search orders by order number
   * @param {string} orderNumber - Order number to search for
   * @returns {Promise<Object>} - API response
   */
  async searchOrderByNumber(orderNumber) {
    try {
      console.log('🔍 Searching for order by number:', orderNumber)
      
      if (!orderNumber) {
        throw new Error('Order number is required')
      }

      const params = {
        orderStatus: 'completed',
        orderNumber: orderNumber.trim()
      }
      
      const response = await this.getAllOrders(params)
      
      console.log('✅ Order search response:', response)
      
      // Extract the first order from the results if found
      if (response.success && response.data?.orders?.length > 0) {
        const order = response.data.orders[0]
        console.log('✅ Order found:', order)
        return {
          success: true,
          data: { order }
        }
      } else {
        console.log('❌ No order found with number:', orderNumber)
        return {
          success: false,
          message: 'Order not found'
        }
      }
    } catch (error) {
      console.error('❌ Error searching order by number:', error)
      throw error
    }
  }

  /**
   * Search completed orders by partial order number for dropdown suggestions
   * @param {string} partialOrderNumber - Partial order number to search for
   * @param {number} limit - Maximum number of results to return
   * @returns {Promise<Object>} - API response with matching orders
   */
  async searchCompletedOrderNumbers(partialOrderNumber, limit = 10) {
    try {
      console.log('🔍 Searching completed orders by partial number:', partialOrderNumber)
      
      if (!partialOrderNumber || partialOrderNumber.length < 3) {
        return {
          success: true,
          data: { orders: [] }
        }
      }

      const params = {
        orderStatus: 'completed',
        limit,
        // Note: This assumes the API supports partial matching on orderNumber
        // If not, we'll need to fetch more orders and filter client-side
        orderNumber: partialOrderNumber.trim()
      }
      
      const response = await this.getAllOrders(params)
      
      console.log('✅ Order search suggestions response:', response)
      return response
    } catch (error) {
      console.error('❌ Error searching order suggestions:', error)
      throw error
    }
  }

  /**
   * Get order type options
   * @returns {Array} - Array of order type options
   */
  getOrderTypeOptions() {
    return [
      { value: 'dine-in', label: 'Dine-in' },
      { value: 'takeaway', label: 'Takeaway' },
      { value: 'delivery', label: 'Delivery' }
    ]
  }
}

export default new OrderService()
